// Curated PGN collections (plan Items D1/D2). The collections are vendored static
// assets under `public/collections/` — a `manifest.json` index plus one `.pgn`
// per collection. This module lazily fetches + parses them (nothing is bundled
// into the initial payload) and renders the collection list on the Study landing.

import { esc } from "../dom.ts";
import { getLang, t, tp } from "../i18n/index.ts";
import { parseGames, type ImportedGame } from "./pgnImport.ts";
import { collapsibleSection } from "../ui/collapsible.ts";

export type CollectionGroup = "classic" | "modern";

export interface CollectionInfo {
  slug: string;
  title: string;
  description?: string;
  /** Slovenian variants (plan B2); the EN fields are the fallback. */
  title_sl?: string;
  description_sl?: string;
  file: string;
  count: number;
  group: CollectionGroup;
}

/** The manifest title/description in the active language (EN fallback). */
export function collectionTitle(c: CollectionInfo): string {
  return (getLang() === "sl" && c.title_sl) || c.title;
}

export function collectionDescription(c: CollectionInfo): string | undefined {
  return (getLang() === "sl" && c.description_sl) || c.description;
}

let manifestCache: CollectionInfo[] | null = null;
const gamesCache = new Map<string, ImportedGame[]>();

/** Fetch (and memoise) the collections manifest. `no-cache` forces a
 *  conditional revalidation: the URL is stable and served without
 *  Cache-Control, so browsers would otherwise serve it heuristically stale
 *  for hours after a deploy. */
export async function loadManifest(): Promise<CollectionInfo[]> {
  if (manifestCache) return manifestCache;
  const res = await fetch("/collections/manifest.json", { cache: "no-cache" });
  if (!res.ok) throw new Error(`manifest ${res.status}`);
  const list = (await res.json()) as unknown;
  manifestCache = Array.isArray(list) ? (list as CollectionInfo[]) : [];
  return manifestCache;
}

/** Fetch + parse one collection's games (memoised by slug). */
export async function loadCollection(
  slug: string,
): Promise<{ info: CollectionInfo; games: ImportedGame[] }> {
  const manifest = await loadManifest();
  const info = manifest.find((c) => c.slug === slug);
  if (!info) throw new Error("Unknown collection");
  const cached = gamesCache.get(slug);
  if (cached) return { info, games: cached };
  const res = await fetch(`/collections/${info.file}`);
  if (!res.ok) throw new Error(`collection ${res.status}`);
  const { games } = parseGames(await res.text());
  gamesCache.set(slug, games);
  return { info, games };
}

/** Nested into native, keyboard-accessible collapsible sections — same
 *  pattern as the Openings panel. */
export async function renderCollections(box: HTMLElement): Promise<void> {
  try {
    const list = await loadManifest();
    if (list.length === 0) {
      box.innerHTML = `<p class="fine">${t("collections.none")}</p>`;
      return;
    }
    box.replaceChildren();
    const groups: { group: CollectionGroup; label: string; help: string }[] = [
      { group: "classic", label: t("collections.classicGames"), help: t("collections.classicGamesHelp") },
      { group: "modern", label: t("collections.modernGames"), help: t("collections.modernGamesHelp") },
    ];
    for (const g of groups) {
      const inGroup = list.filter((c) => c.group === g.group);
      if (inGroup.length === 0) continue;
      const details = collapsibleSection(`collection:${g.group}`, g.label, g.help, inGroup.length);
      const body = details.querySelector<HTMLElement>(".section-body")!;
      for (const c of inGroup) body.append(collectionCard(c));
      box.append(details);
    }
    // A collection with a missing/unknown group (e.g. a stale cached manifest
    // predating the field) must still show up — flat, after the sections.
    const leftover = list.filter((c) => !groups.some((g) => g.group === c.group));
    for (const c of leftover) box.append(collectionCard(c));
  } catch {
    box.innerHTML = `<p class="fine">${t("collections.none")}</p>`;
  }
}

function collectionCard(c: CollectionInfo): HTMLElement {
  const a = document.createElement("a");
  a.className = "game-row collection-card";
  a.setAttribute("data-link", "");
  a.href = `/study/collection/${encodeURIComponent(c.slug)}`;
  a.innerHTML =
    `<span class="opp">${esc(collectionTitle(c))}</span>` +
    `<span class="meta">${esc(collectionDescription(c) ?? "")}${c.count ? ` · ${esc(tp("plural.games", c.count))}` : ""}</span>`;
  return a;
}
